import * as THREE from 'three';

class ThreeEngine {
    constructor() {
        this.scene = null;
        this.allToothObjects = null;
        this.renderer = null;
        this.controls = null;
        this.cameras = null;
        this.controlManager = null;
        this.mandiGroup = null;
        this.tempGroup = null;
        this.comparativeSceneGroup = null;
        this.overlayGroups = null;
        this.center = null;
        
        // SceneBuilder properties
        this.allMandiStuff = [];
        this.allMandiTeeth = [];
        this.mandiMarkupGroup = null;
        this.marks = null;
        this.allAttachments = null;
        this.strippings = null;
        this.mixer = null;
        this.action = null;
        this.maxiExtraDataComparative = null;
        this.mandiExtraDataComparative = null;
        
        // Material properties
        this.coneMaterial = null;
        
        // Project properties
        this.projectId = 0;
    }

    setScene(nextScene) {
        this.scene = nextScene;
    }

    getScene() {
        return this.scene;
    }

    requireScene() {
        if (!this.scene) {
            throw new Error('ThreeEngine scene is not initialized');
        }
        return this.scene;
    }

    setAllToothObjects(objects) {
        this.allToothObjects = objects;
    }

    getAllToothObjects() {
        return this.allToothObjects;
    }

    setRenderer(nextRenderer) {
        this.renderer = nextRenderer;
    }

    getRenderer() {
        return this.renderer;
    }

    setControls(nextControls) {
        this.controls = nextControls;
    }

    getControls() {
        return this.controls;
    }

    setControlManager(manager) {
        this.controlManager = manager;
    }

    getControlManager() {
        return this.controlManager;
    }

    setupControls(camera, rendererDomElement) {
        if (!this.controlManager) return null;
        this.controlManager.setup(camera, rendererDomElement);
        this.setControls(this.controlManager.getCurrentRef());
        return this.controls;
    }

    updateControls() {
        if (!this.controlManager) return;
        this.controlManager.update();
    }

    targetCenter() {
        if (!this.controlManager) return;
        this.controlManager.targetCenter();
    }

    changeControl() {
        if (!this.controlManager) return;
        this.controlManager.changeControl();
        this.setControls(this.controlManager.getCurrentRef());
    }

    setCameras(nextCameras) {
        this.cameras = nextCameras;
    }

    getCameras() {
        return this.cameras;
    }

    getCamera(name) {
        if (!this.cameras) return null;
        return this.cameras[name];
    }

    setMandiGroup(group) {
        this.mandiGroup = group;
    }

    getMandiGroup() {
        return this.mandiGroup;
    }

    setTempGroup(group) {
        this.tempGroup = group;
    }

    getTempGroup() {
        return this.tempGroup;
    }

    setComparativeSceneGroup(group) {
        this.comparativeSceneGroup = group;
    }

    getComparativeSceneGroup() {
        return this.comparativeSceneGroup;
    }

    setCenter(vector) {
        this.center = vector;
    }

    getCenter() {
        return this.center;
    }

    setOverlayGroups(groups) {
        this.overlayGroups = groups;
    }

    getOverlayGroups() {
        return this.overlayGroups;
    }

    getOverlayGroup(name) {
        if (!this.overlayGroups) return null;
        return this.overlayGroups[name];
    }

    // SceneBuilder properties
    setAllMandiStuff(array) {
        this.allMandiStuff = array;
    }

    getAllMandiStuff() {
        return this.allMandiStuff;
    }

    addToAllMandiStuff(item) {
        if (!this.allMandiStuff) this.allMandiStuff = [];
        this.allMandiStuff.push(item);
    }

    setAllMandiTeeth(array) {
        this.allMandiTeeth = array;
    }

    getAllMandiTeeth() {
        return this.allMandiTeeth;
    }

    setMandiMarkupGroup(group) {
        this.mandiMarkupGroup = group;
    }

    getMandiMarkupGroup() {
        return this.mandiMarkupGroup;
    }

    setMarks(marksObj) {
        this.marks = marksObj;
    }

    getMarks() {
        return this.marks;
    }

    setAllAttachments(attachments) {
        this.allAttachments = attachments;
    }

    getAllAttachments() {
        return this.allAttachments;
    }

    setStrippings(strippingsObj) {
        this.strippings = strippingsObj;
    }

    getStrippings() {
        return this.strippings;
    }

    setMixer(mixerObj) {
        this.mixer = mixerObj;
    }

    getMixer() {
        return this.mixer;
    }

    setAction(actionObj) {
        this.action = actionObj;
    }

    getAction() {
        return this.action;
    }

    setMaxiExtraDataComparative(data) {
        this.maxiExtraDataComparative = data;
    }

    getMaxiExtraDataComparative() {
        return this.maxiExtraDataComparative;
    }

    setMandiExtraDataComparative(data) {
        this.mandiExtraDataComparative = data;
    }

    getMandiExtraDataComparative() {
        return this.mandiExtraDataComparative;
    }

    // Material properties
    setConeMaterial(material) {
        this.coneMaterial = material;
    }

    getConeMaterial() {
        if (!this.coneMaterial) {
            // Create default cone material if not set
            this.coneMaterial = new THREE.MeshStandardMaterial({
                color: 0xF86600,
                metalness: 0.35,
                roughness: 0.5,
                side: THREE.DoubleSide
            });
        }
        return this.coneMaterial;
    }

    // Project properties
    setProjectId(id) {
        this.projectId = id;
    }

    getProjectId() {
        return this.projectId;
    }

    dispose() {
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.forceContextLoss();
            if (this.renderer.domElement && this.renderer.domElement.parentNode) {
                this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
            }
            this.renderer = null;
        }

        if (this.scene) {
            this.scene.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(m => {
                            if (m.map) m.map.dispose();
                            m.dispose();
                        });
                    } else {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                }
            });
            this.scene = null;
        }

        if (this.controlManager && this.controlManager.dispose) {
            this.controlManager.dispose();
        }

        if (this.mixer) {
            this.mixer.stopAllAction();
            this.mixer.uncacheRoot(this.mixer.getRoot());
            this.mixer = null;
        }

        if (this.coneMaterial) {
            this.coneMaterial.dispose();
            this.coneMaterial = null;
        }

        this.allToothObjects = null;
        this.controls = null;
        this.controlManager = null;
        this.cameras = null;
        this.mandiGroup = null;
        this.tempGroup = null;
        this.comparativeSceneGroup = null;
        this.overlayGroups = null;
        this.center = null;
        this.allMandiStuff = [];
        this.allMandiTeeth = [];
        this.mandiMarkupGroup = null;
        this.marks = null;
        this.allAttachments = null;
        this.strippings = null;
        this.action = null;
        this.maxiExtraDataComparative = null;
        this.mandiExtraDataComparative = null;
    }
}

export const threeEngine = new ThreeEngine();
export { ThreeEngine };
